import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import { useAppSelector } from "@redux/hooks";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import { GenericButton } from "@ui-components/Button/GenericButton";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import { SkeletonTableRows } from "@ui-components/Skeleton/GenericSkeleton";
import {
  buildPredicate,
  type ParsedFilter,
} from "kontrolEdge/helpers/advancedFilters";
import { normalizeFilters } from "kontrolEdge/helpers/normalizeFilters";
import { makeResourceCatalogFields } from "../resourceCatalogFieldSpec";
import { useServiceLookup } from "../hooks";
import {
  getAllowedProductIds,
  listMergedTagsManagerServices,
  productLabelForServiceType,
  resolveTagsCatalogListParams,
} from "../requests/productAdapters";
import { getTagCatalogResourcesList } from "../requests/request";
import type { AssignmentDraft, ResourceListItem } from "../types";

interface Props {
  /** Current draft assignments (controlled by the parent page). */
  drafts: AssignmentDraft[];
  /** Update the parent's draft list. */
  onChange: (next: AssignmentDraft[]) => void;
  /**
   * When editing a tag with a numeric server id, resources already linked
   * to that tag can be excluded from the catalog (see `excludeTaggedBy`).
   */
  excludeTaggedByTagId?: number;
  /**
   * Numeric tenant the form is pinned to — chosen via the customer selector
   * in step 1 (partner id or a child customer id). Drives the client-side
   * filter that keeps the picker to a single tenant.
   */
  selectedTenantId: number;
  /**
   * Wire `?tenantId=` for the catalog fetch — `undefined` means "omit the
   * override" (partner / partner-view), a number scopes to that tenant.
   * Derived by the parent so the per-role rules (Global Admin always
   * forwards) live in one place.
   */
  wireTenantId?: number;
  /**
   * Whether to render the "Customer" column in the catalog picker. The
   * parent sets this `true` only when the tag is being created at the
   * partner level (Partner Account selected, no specific customer) — the
   * backend then returns resources spanning `[partner + children]`, so the
   * owning customer of each row matters. When a specific customer is
   * selected (or the user IS a customer), it's hidden: every row belongs to
   * the same known tenant.
   */
  showCustomerColumn?: boolean;
}

const composeKey = (serviceType: string, resourceId: string) =>
  `${serviceType}::${resourceId}`;

const CATALOG_PAGE_SIZE = 100;

const TagAssignmentsSection: React.FC<Props> = ({
  drafts,
  onChange,
  excludeTaggedByTagId,
  selectedTenantId,
  wireTenantId,
  showCustomerColumn = false,
}) => {
  const api = useAxiosAuth0();
  // `?tenantId=` for the catalog fetch — owned by the parent (TagFormPage,
  // step 1's customer selector), not the global SideNav lens.
  const tenantId = wireTenantId;
  const { lookup } = useServiceLookup();
  const productList = useAppSelector((s) => s.userProfile.ProductList);
  const allowedProductIds = useMemo(
    () => getAllowedProductIds(productList),
    [productList]
  );

  const mergedServices = useMemo(
    () => listMergedTagsManagerServices(allowedProductIds),
    [allowedProductIds]
  );
  const [activeServiceType, setActiveServiceType] = useState<string>(
    mergedServices[0]?.type ?? ""
  );

  useEffect(() => {
    if (
      activeServiceType &&
      !mergedServices.find((s) => s.type === activeServiceType)
    ) {
      setActiveServiceType(mergedServices[0]?.type ?? "");
    }
  }, [mergedServices, activeServiceType]);

  const activeMergedService = useMemo(
    () => mergedServices.find((s) => s.type === activeServiceType),
    [mergedServices, activeServiceType]
  );

  const [resources, setResources] = useState<ResourceListItem[]>([]);
  const [resourceLoading, setResourceLoading] = useState(false);
  const [resourceError, setResourceError] = useState<string | null>(null);
  // The load loop below fetches every page for the selected service type
  // once; `visibleResources` (memo below) then narrows by name (client-side)
  // and by the selected tenant. The tenant scoping is primarily server-side
  // (the catalog is fetched with `?tenantId=`), but for the partner case the
  // backend expands the read to `[partner + children]`, so we also keep a
  // client-side `ownerTenantId === selectedTenantId` guard as a safety net.
  const [activeFilters, setActiveFilters] = useState<ParsedFilter[]>([]);
  const [committedFilterCount, setCommittedFilterCount] = useState(0);
  const hasFilterChips = committedFilterCount > 0;
  const [pickedIds, setPickedIds] = useState<Set<string>>(new Set());
  // Lazy-load the §6 catalog: the picker card is always visible, but controls
  // stay gated until the user clicks the Service selector ("Add another
  // service" flow). When the tag has no resources yet (create / empty), the
  // picker starts enabled so the user can pick a service immediately.
  const [pickerOpen, setPickerOpen] = useState(drafts.length === 0);

  const serviceDropdownItems = useMemo(
    () =>
      mergedServices.map((s) => `${s.label} — ${s.productLabel}`),
    [mergedServices]
  );

  const serviceLabelToType = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of mergedServices) {
      m.set(`${s.label} — ${s.productLabel}`, s.type);
    }
    return m;
  }, [mergedServices]);

  const activeServiceLabel = useMemo(() => {
    if (!activeMergedService) return "";
    return `${activeMergedService.label} — ${activeMergedService.productLabel}`;
  }, [activeMergedService]);

  // Load resources from tags catalog API when service or search changes.
  // Gated on `pickerOpen` so the catalog is only fetched once the user opens
  // the "Add resources" picker (lazy-load — see `pickerOpen`).
  useEffect(() => {
    if (!pickerOpen) {
      return;
    }
    if (!activeServiceType) {
      setResources([]);
      return;
    }
    const catalogParams = resolveTagsCatalogListParams(activeServiceType);
    if (!catalogParams) {
      setResources([]);
      setResourceError(
        "This service is not mapped to the tags catalog API yet."
      );
      setResourceLoading(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setResourceLoading(true);
      setResourceError(null);
      try {
        const collected: ResourceListItem[] = [];
        let offset = 0;
        const excludeOpt =
          excludeTaggedByTagId != null && Number.isFinite(excludeTaggedByTagId)
            ? { excludeTaggedBy: excludeTaggedByTagId }
            : {};

        for (;;) {
          const res = await getTagCatalogResourcesList(
            api,
            catalogParams.mount,
            {
              module: catalogParams.module,
              resourceType: catalogParams.resourceType,
              limit: CATALOG_PAGE_SIZE,
              offset,
              ...excludeOpt,
              tenantId,
            }
          );
          const envelope = res.data;
          if (!envelope.success) {
            throw new Error(envelope.message || "Catalog request failed");
          }
          const page = envelope.data;
          for (const row of page.items) {
            // Backend v2 ships the owning tenant as flat `tenantId` +
            // `tenantName` on every catalog row. Map to the FE's internal
            // `customer.name` shape so the rest of the picker (header
            // row + Customer cell) keeps rendering without changes. The
            // catalog endpoint doesn't expose chargebeeCustomerId today,
            // so the secondary "ID:" line in the cell is naturally hidden.
            const tenantDisplay =
              typeof row.tenantName === "string" && row.tenantName.trim() !== ""
                ? row.tenantName
                : null;
            collected.push({
              id: String(row.id),
              label: row.label,
              subtitle: row.subtitle,
              resourceType: row.resourceType,
              ownerTenantId:
                typeof row.tenantId === "number" ? row.tenantId : null,
              customer: tenantDisplay ? { name: tenantDisplay } : null,
            });
          }
          offset += page.items.length;
          if (offset >= page.total || page.items.length === 0) {
            break;
          }
        }

        if (!cancelled) {
          setResources(collected);
          setPickedIds(new Set());
        }
      } catch (err) {
        if (!cancelled) {
          setResources([]);
          // H-01: a 403 means the resource catalog belongs to a different
          // tenant than the caller's JWT. Surface a specific message so
          // the user understands it's an access issue, not a service error.
          const status =
            err != null &&
            typeof err === "object" &&
            "response" in err &&
            typeof (err as { response?: unknown }).response === "object" &&
            (err as { response: { status?: unknown } }).response != null
              ? (err as { response: { status?: unknown } }).response.status
              : undefined;
          setResourceError(
            status === 403
              ? "You don't have access to resources in this tenant."
              : "Couldn't load resources for this service."
          );
        }
      } finally {
        if (!cancelled) setResourceLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [pickerOpen, activeServiceType, api, excludeTaggedByTagId, tenantId]);

  const draftKeys = useMemo(
    () => new Set(drafts.map((d) => composeKey(d.serviceType, d.resourceId))),
    [drafts]
  );

  const catalogFields = useMemo(
    () => makeResourceCatalogFields(showCustomerColumn),
    [showCustomerColumn]
  );
  const catalogSuggestedKeys = useMemo(
    () => catalogFields.map((field) => field.label),
    [catalogFields]
  );
  const normalizedCatalogFilters = useMemo(
    () =>
      normalizeFilters<ResourceListItem>(activeFilters, catalogFields, {
        defaultStringToContains: true,
        quotedStringsExact: true,
      }),
    [activeFilters, catalogFields]
  );
  const catalogSearchPredicate = useMemo(
    () => buildPredicate<ResourceListItem>(normalizedCatalogFilters, catalogFields),
    [normalizedCatalogFilters, catalogFields]
  );

  // Client-side narrowing of the loaded list via GenericSearchbar (name,
  // subscription id, and — for partner-level tags — customer).
  //
  // The tenant scoping is fully server-side: the catalog (§6) is fetched with
  // the tag's wire `?tenantId=`, which the backend expands per role —
  // `[partner + children]` for a partner-owned tag (multi-customer add) or a
  // single tenant for a customer-owned tag. So whatever the catalog returns is
  // exactly what's attachable; we must NOT re-filter by a single tenant here
  // (that would break the partner-account multi-customer case).
  const visibleResources = useMemo(() => {
    if (activeFilters.length === 0) return resources;
    return resources.filter(catalogSearchPredicate);
  }, [resources, activeFilters, catalogSearchPredicate]);

  // True when the catalog returned rows but they all belong to OTHER tenants
  // than the selected one (typical "Partner Account" case: the backend expands
  // the read to [partner + children], but only the partner's own resources are
  // taggable under a partner-level tag). Drives a clearer empty-state message
  // than the generic "no match".
  const hiddenByTenantScope = useMemo(() => {
    if (resources.length === 0 || visibleResources.length > 0) return false;
    if (activeFilters.length > 0) return false;
    return resources.some(
      (r) => r.ownerTenantId != null && r.ownerTenantId !== selectedTenantId
    );
  }, [resources, visibleResources, activeFilters, selectedTenantId]);

  const togglePicked = useCallback(
    (resourceId: string) => {
      setPickedIds((prev) => {
        const next = new Set(prev);
        if (next.has(resourceId)) next.delete(resourceId);
        else next.add(resourceId);
        return next;
      });
    },
    []
  );

  const handleAttachSelection = useCallback(() => {
    if (pickedIds.size === 0) return;
    const additions: AssignmentDraft[] = Array.from(pickedIds)
      .filter((id) => !draftKeys.has(composeKey(activeServiceType, id)))
      .map((id) => {
        const item = resources.find((r) => r.id === id);
        return {
          serviceType: activeServiceType,
          resourceId: id,
          resourceLabel: item?.label ?? null,
          resourceSubtitle: item?.subtitle ?? null,
          ownerTenantName: item?.customer?.name ?? null,
        };
      });
    if (additions.length === 0) return;
    onChange([...drafts, ...additions]);
    setPickedIds(new Set());
    // Keep the Selected-resources tab in sync with the service the user
    // just attached from the picker (e.g. Internet), not whichever tab
    // was active before.
    setActiveDraftTab(activeServiceType);
  }, [activeServiceType, drafts, draftKeys, pickedIds, resources, onChange]);

  const handleRemove = useCallback(
    (key: string) => {
      onChange(
        drafts.filter((d) => composeKey(d.serviceType, d.resourceId) !== key)
      );
    },
    [drafts, onChange]
  );

  // Group draft assignments by service so the user sees them clustered.
  const draftsByService = useMemo(() => {
    const grouped = new Map<string, AssignmentDraft[]>();
    for (const d of drafts) {
      const arr = grouped.get(d.serviceType) ?? [];
      arr.push(d);
      grouped.set(d.serviceType, arr);
    }
    return Array.from(grouped.entries());
  }, [drafts]);

  const distinctServiceCount = draftsByService.length;

  // --- Tabs in "Selected resources" — one tab per service-type that has drafts.
  const [activeDraftTab, setActiveDraftTab] = useState<string | null>(
    draftsByService[0]?.[0] ?? null
  );

  // Keep the active tab valid when drafts change (e.g. the active service
  // is removed entirely, or the first draft is added).
  useEffect(() => {
    if (draftsByService.length === 0) {
      if (activeDraftTab !== null) setActiveDraftTab(null);
      return;
    }
    const stillExists = draftsByService.find(
      ([type]) => type === activeDraftTab
    );
    if (!stillExists) {
      setActiveDraftTab(draftsByService[0][0]);
    }
  }, [draftsByService, activeDraftTab]);

  const handleRemoveServiceTab = useCallback(
    (serviceType: string) => {
      onChange(drafts.filter((d) => d.serviceType !== serviceType));
    },
    [drafts, onChange]
  );

  /** Tab click syncs the Service dropdown to that service type for quick adds. */
  const handleTabClick = useCallback((serviceType: string) => {
    setActiveDraftTab(serviceType);
    setActiveServiceType(serviceType);
  }, []);

  const activeTabItems = useMemo(() => {
    if (!activeDraftTab) return [];
    return drafts.filter((d) => d.serviceType === activeDraftTab);
  }, [drafts, activeDraftTab]);

  const addButtonLabel =
    pickedIds.size === 0
      ? "Add"
      : `Add ${pickedIds.size} ${
          activeMergedService?.singularLabel.toLowerCase() ?? "item"
        }${pickedIds.size === 1 ? "" : "s"}`;

  const catalogSearchbar = (
    <GenericSearchbar
      placeholder="Find by name, subscription ID or customer"
      suggestedKeys={catalogSuggestedKeys}
      onFiltersChange={setActiveFilters}
      onCommittedFilterCountChange={setCommittedFilterCount}
      liveFiltering
      debounceMs={200}
      disabled={!pickerOpen}
    />
  );

  const attachButton = (
    <GenericButton
      label={addButtonLabel}
      variant="brand"
      size="md"
      disabled={!pickerOpen || pickedIds.size === 0 || !activeServiceType}
      onClick={handleAttachSelection}
    />
  );

  const serviceDropdown = (
    <GenericDropdown
      fixedPosition={true}
      dropdownMaxHeightClass="max-h-48"
      label="Service"
      items={serviceDropdownItems}
      value={activeServiceLabel}
      defaultLabel="Select a service"
      emptyItemsMessage="No services available"
      onSelect={(pickedLabel) => {
        const nextType = serviceLabelToType.get(pickedLabel);
        if (nextType) setActiveServiceType(nextType);
      }}
      disabled={mergedServices.length === 0}
      widthClass="w-full"
      minWidthClass="min-w-0 md:min-w-[260px]"
      maxWidthClass="max-w-full"
      size="md"
      triggerHeightClass="!py-2"
    />
  );

  return (
    <div className="space-y-5">
      <div className="w-full">
        <h3 className="text-md font-semibold text-semantic-text-primary">
          Resources
        </h3>
        <p className="text-xs text-semantic-text-secondary mt-1">
          A tag can group resources from multiple services and products. Pick a
          service (ports, reflectors, BGP sessions, etc.) from the combined
          list—no separate product switch—then add resources below.
        </p>
      </div>

      {/* --- Selected resources (top, tabs by service) --- */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-semantic-text-primary">
            Selected resources
          </h4>
          <span className="text-xs text-semantic-text-secondary">
            {drafts.length} resource{drafts.length === 1 ? "" : "s"} ·{" "}
            {distinctServiceCount} service
            {distinctServiceCount === 1 ? "" : "s"}
          </span>
        </div>
        {drafts.length === 0 ? (
          <div className="text-center py-6 border border-dashed border-semantic-border-secondary rounded-md">
            <p className="text-sm text-semantic-text-secondary">
              No resources selected yet. Add some from the picker below.
            </p>
          </div>
        ) : (
          <div className="border border-semantic-border-secondary rounded-md overflow-hidden bg-semantic-bg-primary">
            {/* Tabs row — overflow-y-hidden is required: overflow-x-auto alone
               makes the browser promote overflow-y (default visible) to auto,
               so the active tab's `-mb-px`/`border-b-2` overhang would trigger
               a spurious vertical scrollbar. */}
            <div className="flex items-stretch overflow-x-auto overflow-y-hidden custom-scrollbar bg-semantic-bg-secondary border-b border-semantic-border-secondary">
              {draftsByService.map(([serviceType, items]) => {
                const svcLabel =
                  lookup(serviceType)?.service.label ?? serviceType;
                const isActive = serviceType === activeDraftTab;
                return (
                  <div
                    key={serviceType}
                    className={`flex items-center gap-1.5 border-r border-semantic-border-secondary ${
                      isActive
                        ? "bg-semantic-bg-primary border-b-2 border-b-semantic-border-brand-secondary -mb-px"
                        : "hover:bg-semantic-state-active"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => handleTabClick(serviceType)}
                      className={`px-3 py-2 text-sm font-medium ${
                        isActive
                          ? "text-semantic-text-brand"
                          : "text-semantic-text-primary"
                      }`}
                    >
                      {svcLabel}
                      <span className="ml-1.5 text-xs text-semantic-text-secondary">
                        ({items.length})
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveServiceTab(serviceType);
                      }}
                      aria-label={`Remove all ${svcLabel}`}
                      title={`Remove all ${svcLabel}`}
                      className="mr-2 w-5 h-5 flex items-center justify-center rounded text-semantic-text-secondary hover:text-semantic-text-error hover:bg-semantic-state-active text-sm leading-none"
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Active-tab items list — caps at ~3 rows so the panel stays
               compact: from the 4th resource on it scrolls inside the list
               instead of growing the section; 1–3 size to content (no bar). */}
            <div className="max-h-[180px] overflow-y-auto custom-scrollbar divide-y divide-semantic-border-secondary">
              {activeTabItems.length === 0 ? (
                <div className="px-3 py-6 text-sm text-semantic-text-secondary text-center">
                  No resources in this service.
                </div>
              ) : (
                activeTabItems.map((d) => {
                  const key = composeKey(d.serviceType, d.resourceId);
                  const productInfo = productLabelForServiceType(d.serviceType);
                  const subtitle =
                    (d.resourceSubtitle && d.resourceSubtitle.trim()) ||
                    d.resourceId;
                  const customerName = d.ownerTenantName?.trim() || null;
                  const showCustomer =
                    showCustomerColumn && Boolean(customerName);
                  return (
                    <div
                      key={key}
                      className="flex items-start gap-3 px-3 py-2 text-sm w-full"
                    >
                      <div className="min-w-0 flex-1">
                        <span className="text-semantic-text-primary truncate block">
                          {d.resourceLabel || `#${d.resourceId}`}
                        </span>
                        <div className="text-xs text-semantic-text-secondary truncate">
                          <span className="font-mono">{subtitle}</span>
                          {productInfo && (
                            <>
                              <span> - </span>
                              <span>{productInfo.label}</span>
                            </>
                          )}
                        </div>
                        {showCustomer && customerName && (
                          <span className="text-xs text-semantic-text-secondary truncate block">
                            {customerName}
                          </span>
                        )}
                      </div>
                      <div className="ml-auto shrink-0 pl-2">
                        <GenericButton
                          label="Remove"
                          variant="secondary"
                          size="sm"
                          onClick={() => handleRemove(key)}
                        />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {/* --- Add resources picker: always visible; catalog (§6) loads only
           once the user enables the picker via the Service selector. --- */}
      <div className="border border-semantic-border-primary rounded-md p-4 bg-semantic-bg-secondary">
        <div className="flex items-center justify-between gap-3 mb-3">
          <h4 className="text-sm font-semibold text-semantic-text-primary">
            {drafts.length === 0
              ? "Add resources"
              : "Add another service"}
          </h4>
          {activeMergedService && pickerOpen && (
            <span className="text-xs text-semantic-text-secondary truncate max-w-[min(100%,14rem)]">
              {activeMergedService.productLabel}
            </span>
          )}
        </div>

        {/*
         * Picker controls — single grid so GenericSearchbar is never
         * remounted when the first filter chip appears (a conditional tree
         * used to reset internal chip state and drop activeFilters).
         *
         *  - No chips: `items-end` — dropdown trigger, search input, and Add
         *    share a baseline (Service label sits above its control only).
         *  - With chips: invisible Search label matches Service label height;
         *    Add uses `self-stretch` + `items-end` to sit beside the chip row.
         *
         * `<md` collapses to a single column. We MUST drop the dropdown's
         * `min-w-[320px]` because `GenericDropdown` applies its
         * `minWidthClass` to both the trigger and the menu container — 320px
         * overflows a phone container narrower than that.
         */}
        <div
          className={`grid grid-cols-1 gap-3 md:grid-cols-[minmax(320px,460px)_1fr_auto] ${
            hasFilterChips ? "md:items-start" : "items-end"
          }`}
        >
          <div className="relative min-w-0">
            {!pickerOpen && mergedServices.length > 0 && (
              <button
                type="button"
                className="absolute inset-0 z-10 cursor-pointer rounded-sm hover:bg-semantic-state-active/40 transition-colors"
                aria-label="Select a service to add resources"
                data-testid={
                  drafts.length > 0 ? "add-another-service-trigger" : undefined
                }
                onClick={() => setPickerOpen(true)}
              />
            )}
            <div className={!pickerOpen ? "pointer-events-none" : undefined}>
              {serviceDropdown}
            </div>
          </div>
          <div className="min-w-0 w-full">
            {hasFilterChips && (
              <label
                className="mb-1 hidden text-sm font-medium text-semantic-text-primary md:block invisible select-none pointer-events-none"
                aria-hidden
              >
                Search
              </label>
            )}
            {catalogSearchbar}
          </div>
          <div
            className={
              hasFilterChips
                ? "flex justify-end md:self-stretch md:items-end"
                : undefined
            }
          >
            {attachButton}
          </div>
        </div>

        {/*
         * Catalog rows.
         *
         * The previous grid (`grid-cols-[36px_1fr_140px_120px]`) hard-coded
         * px widths for ID + Status, which clipped on narrow viewports and
         * left empty space on wide ones. Replaced with a flex layout:
         *
         *   [chk] [ Name           ] [Subtitle] [Resource pill]
         *         [ subtitle*       ]
         *
         * Behavior on each breakpoint:
         *  - md+   → Name | Subtitle | Resource displayed in three columns
         *            (`md:flex md:items-center md:gap-4`). Resource pill
         *            stays its natural size, Subtitle truncates after Name.
         *  - <md   → Subtitle wraps under Name as a secondary line so the
         *            row never needs horizontal scroll on mobile. The pill
         *            stays right-aligned at the row top.
         *
         * The header mirrors the same column gaps and is hidden on mobile
         * (the row's stacked layout makes labels redundant there).
         */}
        <div className="mt-3 border border-semantic-border-secondary rounded-md overflow-hidden bg-semantic-bg-primary">
          <div className="hidden md:flex items-center gap-4 px-3 py-2 text-xs font-semibold text-semantic-text-secondary uppercase tracking-wide bg-semantic-bg-secondary border-b border-semantic-border-secondary">
            <span className="w-4 flex-shrink-0" aria-hidden />
            {showCustomerColumn && (
              <span className="w-40 flex-shrink-0">Customer</span>
            )}
            <span className="flex-1 min-w-0">Name</span>
            <span className="hidden md:block flex-1 min-w-0">Subscription Id</span>
          </div>
          <div className="max-h-[220px] overflow-y-auto custom-scrollbar divide-y divide-semantic-border-secondary">
            {!pickerOpen ? (
              <div className="px-3 py-8 text-sm text-semantic-text-secondary text-center">
                Select a{" "}
                <span className="font-medium text-semantic-text-primary">
                  Service
                </span>{" "}
                above to browse the catalog and attach additional items.
              </div>
            ) : resourceLoading ? (
              <div className="px-3 py-4" aria-busy="true" aria-label="Loading resources">
                <SkeletonTableRows
                  rows={6}
                  cols={1}
                  rowHeight={14}
                  animated
                />
              </div>
            ) : resourceError ? (
              <div className="px-3 py-6 text-sm text-semantic-text-danger">
                {resourceError}
              </div>
            ) : resources.length === 0 ? (
              <div className="px-3 py-8 text-sm text-semantic-text-secondary text-center">
                No resources returned from the catalog for this service.
              </div>
            ) : visibleResources.length === 0 ? (
              <div className="px-3 py-8 text-sm text-semantic-text-secondary text-center">
                {hiddenByTenantScope ? (
                  <>
                    These resources belong to your customers, not the partner
                    account. Go back to{" "}
                    <span className="font-medium text-semantic-text-primary">
                      Basic information
                    </span>{" "}
                    and select a specific customer to tag its resources.
                  </>
                ) : (
                  <>
                    No resources match the current filters.
                  </>
                )}
              </div>
            ) : (
              visibleResources.map((it) => {
                const key = composeKey(activeServiceType, it.id);
                const isAttached = draftKeys.has(key);
                const isPicked = pickedIds.has(it.id);
                const nameContent = (
                  <>
                    {it.label || `#${it.id}`}
                    {isAttached && (
                      <span className="ml-2 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-semantic-bg-secondary text-semantic-text-secondary">
                        Already added
                      </span>
                    )}
                  </>
                );
                return (
                  <button
                    key={it.id}
                    type="button"
                    onClick={() => !isAttached && togglePicked(it.id)}
                    disabled={isAttached}
                    className={`w-full flex items-start md:items-center gap-3 md:gap-4 px-3 py-2 text-sm text-left transition-colors ${
                      isAttached
                        ? "opacity-50 cursor-not-allowed"
                        : isPicked
                        ? "bg-semantic-state-active"
                        : "hover:bg-semantic-state-active"
                    }`}
                  >
                    <span className="flex items-center pt-0.5 md:pt-0 flex-shrink-0">
                      <input
                        type="checkbox"
                        checked={isPicked || isAttached}
                        readOnly
                        disabled={isAttached}
                        className="h-4 w-4 cursor-pointer accent-semantic-bg-brand-bold-secondary"
                      />
                    </span>
                    {/*
                     * Mobile (<md): Customer + Name + Subtitle stacked
                     * vertically so the row keeps a single column and never
                     * needs horizontal scroll on narrow viewports. The
                     * Customer line only shows for partner-level tags.
                     */}
                    <span className="flex-1 min-w-0 flex flex-col md:hidden">
                      <span className="text-semantic-text-primary truncate">
                        {nameContent}
                      </span>
                      {it.subtitle && (
                        <span className="text-xs text-semantic-text-secondary font-mono truncate">
                          {it.subtitle}
                        </span>
                      )}
                      {showCustomerColumn && (
                        <span className="text-xs text-semantic-text-secondary truncate">
                          Customer: {it.customer?.name || "—"}
                        </span>
                      )}
                    </span>
                    {/*
                     * md+: Customer | Name | Subtitle become siblings that
                     * match the column header layout above. Customer is the
                     * first data column and only rendered for partner-level
                     * tags.
                     */}
                    {showCustomerColumn && (
                      <span className="hidden md:block w-40 flex-shrink-0 text-semantic-text-primary truncate">
                        {it.customer?.name || "—"}
                      </span>
                    )}
                    <span className="hidden md:block flex-1 min-w-0 text-semantic-text-primary truncate">
                      {nameContent}
                    </span>
                    <span className="hidden md:block flex-1 min-w-0 text-xs text-semantic-text-secondary font-mono truncate">
                      {it.subtitle || "—"}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TagAssignmentsSection;
